import { useRef, useEffect } from 'react';

// ── Shared speaking motion for the Nuala characters ─────────────────────────
// One file drives every character. Attach the returned ref to the root <svg>.
//
//   useLipSync({ isSpeaking, speechMarks, speed })
//     • isSpeaking  – boolean (default false). When false the character is
//                     completely static (mouth at its drawn pose, no head bob).
//     • speechMarks – array of word marks, e.g.
//                     [{ time: 47.6, duration: 130.9, value: 'El', wordLength: 2 }, ...]
//                     (time & duration in ms). Drives the mouth open/close.
//     • speed       – optional playback multiplier (default 1).
//
// Inside each character SVG:
//   • the head group is <g className="ls-head"> (everything that should bob)
//   • every mouth part is <path className="lipsync-mouth" style={{ transformBox:
//     'view-box', transformOrigin: '<x>px <y>px' }} /> — the pivot is the
//     character's upper lip so all mouth parts open together as one.

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const s = document.createElement('style');
  s.setAttribute('data-nuala-motion', '');
  s.textContent = [
    '@keyframes nualaHeadBob{0%,100%{transform:translateY(0) rotate(0)}25%{transform:translateY(-2px) rotate(-1.3deg)}50%{transform:translateY(0)}75%{transform:translateY(-1.5px) rotate(1.1deg)}}',
    '.nuala-mascot .ls-head{transform-box:fill-box;transform-origin:50% 100%;}',
    '.nuala-mascot.nuala-speaking .ls-head{animation:nualaHeadBob 1.6s ease-in-out infinite;will-change:transform;}',
    '@media (prefers-reduced-motion:reduce){.nuala-mascot.nuala-speaking .ls-head{animation:none!important}}'
  ].join('\n');
  document.head.appendChild(s);
}

const frac = (x) => x - Math.floor(x);

// Azure marks carry their own `duration`. AWS Polly marks only carry a start
// `time` (plus character offsets `start`/`end`, not timing) — no duration at
// all — so it's derived here from the gap to the next mark, falling back to
// a length-based estimate for the final word.
function withDurations(speechMarks) {
  if (!Array.isArray(speechMarks)) return [];
  return speechMarks.map((m, i) => {
    if (!m || typeof m.time !== 'number' || typeof m.duration === 'number') return m;
    const next = speechMarks[i + 1];
    const wordLength = m.wordLength || (typeof m.value === 'string' ? m.value.length : 3);
    const fallback = Math.max(80, wordLength * 60);
    const duration = next && typeof next.time === 'number' ? Math.max(60, next.time - m.time) : fallback;
    return { ...m, duration };
  });
}

// Stop the head bob without a jump: capture the head's live bob transform,
// halt the keyframe animation, then transition from that captured pose to rest
// so the bob visibly settles into place instead of snapping.
function settleHead(root) {
  const head = root.querySelector('.ls-head');
  if (!head) return;
  const cur = getComputedStyle(head).transform; // live bob pose (animation still on)
  root.classList.remove('nuala-speaking');       // stop the keyframe animation
  head.style.transition = 'none';
  head.style.transform = (cur && cur !== 'none') ? cur : 'none';
  void document.body.offsetHeight;               // flush so the start pose commits
  head.style.transition = 'transform 0.5s cubic-bezier(0.22,0.61,0.36,1)';
  head.style.transform = 'none';
}

// Resume bobbing: drop the inline overrides so the CSS animation takes over.
function resumeHead(root) {
  const head = root.querySelector('.ls-head');
  if (head) { head.style.transition = ''; head.style.transform = ''; }
}

export function useLipSync({ isSpeaking = false, speechMarks = [], speed = 1 } = {}) {
  const rootRef = useRef(null);
  const stateRef = useRef({ cur: 1, t0: 0, raf: 0 });

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.classList.add('nuala-mascot');
    const mouths = root.querySelectorAll('.lipsync-mouth');
    const st = stateRef.current;

    const marks = withDurations(speechMarks).filter(
      (m) => m && typeof m.time === 'number' && typeof m.duration === 'number'
    );

    // Static: not speaking, or nothing to say → settle back to the drawn pose.
    if (!isSpeaking || marks.length === 0) {
      settleHead(root);
      mouths.forEach((m) => { m.style.transform = 'scaleY(1)'; });
      st.cur = 1;
      return undefined;
    }

    root.classList.add('nuala-speaking');
    resumeHead(root);
    st.t0 = performance.now();

    const end = Math.max(...marks.map((m) => m.time + m.duration));

    const loop = (now) => {
      const t = (now - st.t0) * speed;

      // Finished the speech marks — settle back to the static pose and stop the
      // head bob, even though isSpeaking may still be true. settleHead() eases
      // the head from its live bob pose to rest so it doesn't snap.
      if (t >= end) {
        st.cur = 1;

        mouths.forEach((m) => {
          m.style.transform = 'scaleY(1)';
        });

        settleHead(root);
        st.raf = 0;
        return;
      }

      let target = 1;

      if (t >= marks[0].time && t <= end) {
        let inWord = false;
        for (let i = 0; i < marks.length; i++) {
          const m = marks[i];
          if (t >= m.time && t <= m.time + m.duration) {
            inWord = true;
            const p = Math.min(1, Math.max(0, (t - m.time) / Math.max(1, m.duration)));
            const wl = m.wordLength || (m.value ? String(m.value).length : 3);
            const syl = Math.min(5, Math.max(1, Math.round(wl / 2.6)));
            const env = Math.sin(Math.PI * p);
            const flut = 0.5 - 0.5 * Math.cos(2 * Math.PI * syl * p);
            const amp = 0.78 + 0.22 * frac(Math.sin((i + 1) * 12.9898) * 43758.5453);
            let o = env * (0.5 + 0.5 * flut) * amp;
            o = Math.min(1, Math.max(0, o));
            target = 0.35 + o * 1.15;
            break;
          }
        }
        if (!inWord) target = 0.42; // brief closure between words
      }
      st.cur += (target - st.cur) * 0.4;
      const v = st.cur.toFixed(3);
      mouths.forEach((m) => { m.style.transform = 'scaleY(' + v + ')'; });
      st.raf = requestAnimationFrame(loop);
    };
    st.raf = requestAnimationFrame(loop);
    return () => { if (st.raf) cancelAnimationFrame(st.raf); };
  }, [isSpeaking, speechMarks, speed]);

  return rootRef;
}
